import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { Airport } from '@/types';
import { generateArcPoints } from '@/utils';

interface FlightPathProps {
  from: Airport;
  to: Airport;
  color: string;
  showPlane?: boolean;
}

export function FlightPath({ from, to, color, showPlane = true }: FlightPathProps) {
  const pathRef = useRef<THREE.Mesh>(null);
  const glowRef = useRef<THREE.Mesh>(null);
  const planeRef = useRef<THREE.Group>(null);
  const trailRef = useRef<THREE.Mesh>(null);
  const progressRef = useRef(Math.random()); // Random start position
  const trailPositions = useRef<THREE.Vector3[]>([]);

  // Generate arc points
  const geometryData = useMemo(() => {
    // Validate coordinates before generating arc
    if (from?.lat == null || from?.lon == null || to?.lat == null || to?.lon == null ||
        !isFinite(from.lat) || !isFinite(from.lon) || !isFinite(to.lat) || !isFinite(to.lon)) {
      return null;
    }
    
    const points = generateArcPoints(from, to);
    const threePoints = points.map((p) => new THREE.Vector3(...p));
    const curve = new THREE.CatmullRomCurve3(threePoints);

    const pathGeometry = new THREE.TubeGeometry(curve, 64, 0.003, 8, false);
    const glowGeometry = new THREE.TubeGeometry(curve, 64, 0.008, 8, false);

    return { curve, pathGeometry, glowGeometry };
  }, [from, to]);

  // Animate the plane along the path
  useFrame((_, delta) => {
    if (!geometryData || !planeRef.current || !showPlane) return;
    
    const { curve } = geometryData;

    // Smooth progress with easing
    progressRef.current += delta * 0.12;
    if (progressRef.current > 1) {
      progressRef.current = 0;
      trailPositions.current = [];
    }

    const point = curve.getPoint(progressRef.current);
    planeRef.current.position.copy(point);

    // Add current position to trail
    trailPositions.current.push(point.clone());
    if (trailPositions.current.length > 15) {
      trailPositions.current.shift();
    }

    // Update trail geometry
    if (trailRef.current && trailPositions.current.length > 1) {
      const trailCurve = new THREE.CatmullRomCurve3(trailPositions.current);
      const trailGeometry = new THREE.TubeGeometry(trailCurve, 20, 0.004, 8, false);
      trailRef.current.geometry.dispose();
      trailRef.current.geometry = trailGeometry;
    }

    // Orient plane to follow path with banking
    if (progressRef.current < 0.99) {
      const tangent = curve.getTangent(progressRef.current);
      const nextTangent = curve.getTangent(Math.min(progressRef.current + 0.01, 1));
      
      // Calculate banking angle based on turn direction
      const cross = new THREE.Vector3().crossVectors(tangent, nextTangent);
      const bankAngle = cross.length() * 2;
      
      const lookAtPoint = new THREE.Vector3().copy(point).add(tangent);
      planeRef.current.lookAt(lookAtPoint);
      planeRef.current.rotateZ(bankAngle);
    }
  });

  // Return null if invalid coordinates
  if (!geometryData) return null;

  const { pathGeometry, glowGeometry } = geometryData;
  const colorValue = new THREE.Color(color);

  return (
    <>
      {/* Main path line */}
      <mesh ref={pathRef} geometry={pathGeometry}>
        <meshBasicMaterial
          color={colorValue}
          transparent
          opacity={0.8}
        />
      </mesh>

      {/* Glow effect */}
      <mesh ref={glowRef} geometry={glowGeometry}>
        <meshBasicMaterial
          color={colorValue}
          transparent
          opacity={0.3}
        />
      </mesh>

      {/* Animated plane */}
      {showPlane && (
        <>
          {/* Glowing trail */}
          <mesh ref={trailRef}>
            <meshBasicMaterial
              color={colorValue}
              transparent
              opacity={0.5}
            />
          </mesh>
          
          {/* Plane */}
          <group ref={planeRef}>
            <PlaneModel color={color} />
          </group>
        </>
      )}
    </>
  );
}

// Simple plane shape that points forward
function PlaneModel({ color }: { color: string }) {
  const shape = useMemo(() => {
    const s = new THREE.Shape();
    // Draw airplane pointing right (forward)
    s.moveTo(0.02, 0); // nose
    s.lineTo(0.01, 0.003);
    s.lineTo(-0.015, 0.003); // wing tip
    s.lineTo(-0.015, 0.001);
    s.lineTo(-0.01, 0.001);
    s.lineTo(-0.018, 0.0005); // tail wing
    s.lineTo(-0.02, 0.001);
    s.lineTo(-0.02, -0.001);
    s.lineTo(-0.018, -0.0005); // tail wing other side
    s.lineTo(-0.01, -0.001);
    s.lineTo(-0.015, -0.001);
    s.lineTo(-0.015, -0.003); // wing tip
    s.lineTo(0.01, -0.003);
    s.lineTo(0.02, 0); // back to nose
    return s;
  }, []);

  const colorValue = new THREE.Color(color);

  return (
    <group rotation={[0, 0, -Math.PI / 2]}> {/* Rotate to point in travel direction */}
      <mesh>
        <shapeGeometry args={[shape]} />
        <meshBasicMaterial 
          color="white" 
          side={THREE.DoubleSide}
        />
      </mesh>
      {/* Colored glow */}
      <mesh scale={1.3}>
        <shapeGeometry args={[shape]} />
        <meshBasicMaterial 
          color={colorValue} 
          transparent
          opacity={0.5}
          side={THREE.DoubleSide}
        />
      </mesh>
      <pointLight color={colorValue} intensity={0.6} distance={0.12} />
    </group>
  );
}
